/**
 * The fab feature (M8 plan MC-c3a, MC-c3b): registers `fab.start`, `fab.cancel`, `fab.complete` and `fab.deliver` on
 * the craft hook (`craft/hook.ts`), so `UiActions.craft` runs them. Every fab command acts at the station the order
 * was placed at, while docked there: the module goes to the flown ship's locker, and a cancelled order's delivered
 * inputs go to that station's berth, stamped with the berth feature's owner rule.
 *
 * **Deliver** draws from the hold, then the berth. With `buyMissing` it then buys what the station sells of the rest
 * through `ctx.market.commitGoods` inside `ctx.inFlight` (the one-trade-in-flight lock): each executed fill is booked
 * straight into the order's `delivered` with its credits (`bookFabBuy`), never the hold. The hold/berth part is
 * committed inside the same in-flight work, so a buy refused at once (another trade settling, too few credits, no
 * strategic link) changes nothing, and a refused strategic buy changes nothing either. While an order's buy is in
 * flight, that order refuses cancel, complete and another delivery (`notReady`).
 *
 * **Salvage** (MC-c8): `fab.deliver` with `salvage` (a component id) gives that salvaged component as the recipe's
 * alternative input (`giveSalvage`) and nothing else: the hold's goods wait for a Deliver (PF-3). With `buyMissing` it
 * gives first, then delivers and buys as above. A refused give refuses the whole command.
 *
 * **Remote berth pull** (MC-b7): in the Reach, deliver also draws the player's other open berths within
 * `career.json berth.remotePull.jumps` relay jumps once unlocked (`craft/fabOrder.ts remoteBerths`). The world is
 * read from the Reach runtime (galaxy, relays online now, layouts) and the strategic client's cached view of the local
 * system: a berth in another system has no cached view, so its stamp alone decides its access (D-500, D-507).
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import { siteOfStation } from '../../bridge/m2/stationTabs'
import { m2StationId } from '../../bridge/m2/stations'
import { berthAccess, type BerthData } from '../../career/berth'
import { EPSILON } from '../../career/ops'
import { insufficientFunds, ok, type CmdResult, type CommandError } from '../../career/result'
import type { Career } from '../../career/types'
import {
  bookFabBuy,
  cancelFab,
  completeFab,
  deliverFab,
  fabBuyPlan,
  fabOrder,
  giveSalvage,
  startFab,
  type RemoteBerthWorld,
  type RemotePull,
} from '../../craft/fabOrder'
import { registerCraftHandler, type CraftCommandMap, type CraftCtx, type CraftHandler } from '../../craft/hook'
import type { StationDef, StationId } from '../../data/m2Schemas'
import { isRelayOnline } from '../../reach/state/relays'
import type { ReachCore } from '../../reach/runtime/types'
import type { StationView } from '../../strategic/types'
import type { CmdOutcome } from '../../ui/contracts'
import { berthOwner, berthViewOf, type BerthOwnerOf } from '../berth/feature'

/** After the career runtime (the registry is global, so the order only places it in the list). */
const ORDER = FEATURE_ORDER.career + 1

/** The docked station when it is `stationId`, else the refusal. */
function dockedAt(ctx: CraftCtx, stationId: string): StationDef | CommandError {
  const here = ctx.station
  if (here === null || here.id !== stationId) {
    return { code: 'serviceUnavailable', message: 'Dock at the station that holds this fab order.', detail: { station: stationId } }
  }
  return here
}

function isStation(v: StationDef | CommandError): v is StationDef {
  return 'id' in v
}

/** The docked station when it holds open order `orderId`, else the refusal. */
function orderStation(ctx: CraftCtx, orderId: string): StationDef | CommandError {
  const order = fabOrder(ctx.career(), orderId)
  if (order === null) return { code: 'invalidAmount', message: 'That fab order is not open.', detail: { order: orderId } }
  return dockedAt(ctx, order.stationId)
}

/** The refusal for an order whose buy missing is still in flight. */
function buyInFlight(orderId: string): CommandError {
  return { code: 'notReady', message: 'That fab order is still settling a purchase.', detail: { order: orderId } }
}

/** A Reach station's career id is its site's slug (`sys-33-st0` → `sys_33_st0`): the system slug, then `_st<n>`. */
const SITE_SLUG = /^(.+)_st\d+$/

/**
 * The remote pull world (MC-b7) over the Reach runtime: every galaxy system, the relays online now, and each berth
 * station's system (a Reach site by its slug, a home-cluster station that keeps its M2 id by the home system's layout).
 * Access is `berthAccess` over the station's cached view among `views` (the local system's; `layout` is its
 * layout), else the berth's stamp alone.
 */
export function fabRemoteWorld(
  reach: Pick<ReachCore, 'galaxy' | 'state' | 'layoutOf'>,
  views: readonly StationView[] | null | undefined,
  layout: Parameters<typeof berthViewOf>[2],
  data: BerthData,
  now: number,
): RemoteBerthWorld {
  const { galaxy } = reach
  const bySlug = new Map(galaxy.systems.map((s) => [m2StationId(s.id), s.id]))
  const links: [string, string][] = []
  for (const r of galaxy.relays) if (isRelayOnline(reach.state, galaxy, r.id)) links.push([r.a, r.b])
  return {
    systems: galaxy.systems.map((s) => s.id),
    links,
    systemOf(stationId: StationId): string | null {
      const m = SITE_SLUG.exec(stationId)
      const sys = m === null ? undefined : bySlug.get(m[1])
      if (sys !== undefined) return sys
      return siteOfStation(reach.layoutOf(galaxy.homeSystemId), stationId) === null ? null : galaxy.homeSystemId
    },
    access: (stationId, entry, career) => berthAccess(entry, berthViewOf(stationId, views, layout), career, now, data),
  }
}

/** The remote pull world for a command, or null (no Reach runtime: the hold and the local berth only). */
export type FabRemoteOf = (ctx: CraftCtx) => RemoteBerthWorld | null

/** The four handlers (exported for the tests; the feature registers them). `remoteOf` (default: none) gives MC-b7's world. */
export function createFabHandlers(
  ownerOf: BerthOwnerOf,
  remoteOf: FabRemoteOf = () => null,
): {
  start: CraftHandler<CraftCommandMap['fab.start']>
  cancel: CraftHandler<CraftCommandMap['fab.cancel']>
  complete: CraftHandler<CraftCommandMap['fab.complete']>
  deliver: CraftHandler<CraftCommandMap['fab.deliver']>
} {
  /** Orders with a buy missing in flight. */
  const buying = new Set<string>()
  return {
    start(cmd, ctx): CmdOutcome {
      const here = dockedAt(ctx, cmd.stationId)
      if (!isStation(here)) return ctx.refuse(here)
      const r = startFab(ctx.career(), ctx.data, here, cmd.moduleId, ctx.system)
      if (!r.ok) return ctx.refuse(r.error)
      ctx.commit(r.value, r.effects)
      return ctx.done()
    },
    cancel(cmd, ctx): CmdOutcome {
      const here = orderStation(ctx, cmd.orderId)
      if (!isStation(here)) return ctx.refuse(here)
      if (buying.has(cmd.orderId)) return ctx.refuse(buyInFlight(cmd.orderId))
      const r = cancelFab(ctx.career(), cmd.orderId, ownerOf(here))
      if (!r.ok) return ctx.refuse(r.error)
      ctx.commit(r.value, r.effects)
      return ctx.done()
    },
    complete(cmd, ctx): CmdOutcome {
      const here = orderStation(ctx, cmd.orderId)
      if (!isStation(here)) return ctx.refuse(here)
      if (buying.has(cmd.orderId)) return ctx.refuse(buyInFlight(cmd.orderId))
      const r = completeFab(ctx.career(), cmd.orderId)
      if (!r.ok) return ctx.refuse(r.error)
      ctx.commit(r.value, r.effects)
      return ctx.done()
    },
    deliver(cmd, ctx): CmdOutcome {
      const orderId = cmd.orderId
      const here = orderStation(ctx, orderId)
      if (!isStation(here)) return ctx.refuse(here)
      if (buying.has(orderId)) return ctx.refuse(buyInFlight(orderId))
      const salvage = cmd.salvage
      const world = remoteOf(ctx)
      const remote: RemotePull | null = world === null ? null : { data: ctx.data, world }
      /**
       * Buy missing's stock step: delivers from the hold and berth; with `salvage`, gives that component first (MC-c8),
       * and then a delivery of nothing is fine.
       */
      const restock = (c: Career): CmdResult<Career> => {
        if (salvage === undefined) return deliverFab(c, orderId, remote)
        const given = giveSalvage(c, ctx.data, orderId, salvage, ownerOf(here))
        if (!given.ok) return given
        const r = deliverFab(given.value, orderId, remote)
        return r.ok ? r : ok(given.value)
      }
      if (cmd.buyMissing !== true) {
        // PF-3: a plain Give hands over the salvaged component alone; the hold's goods wait for Deliver.
        const c = ctx.career()
        const r = salvage === undefined ? deliverFab(c, orderId, remote) : giveSalvage(c, ctx.data, orderId, salvage, ownerOf(here))
        if (!r.ok) return ctx.refuse(r.error)
        ctx.commit(r.value, r.effects)
        return ctx.done()
      }
      const market = ctx.market
      if (market === null) {
        return ctx.refuse({
          code: 'serviceUnavailable',
          message: 'Buy missing needs the station market, and there is no strategic link here.',
          detail: { order: orderId, reason: 'noMarket' },
        })
      }
      // Checked now so a refusal changes nothing; done again inside the trade lock on the Career as it is then.
      const stocked = restock(ctx.career())
      if (!stocked.ok && salvage !== undefined) return ctx.refuse(stocked.error)
      const start = stocked.ok ? stocked.value : ctx.career()
      const plan = fabBuyPlan(start, orderId, market)
      if (plan.length === 0) {
        if (!stocked.ok) {
          return ctx.refuse({
            code: 'notInCargo',
            message: 'Nothing in your hold or this berth that the order still needs, and the station sells none of it.',
            detail: { order: orderId, reason: 'nothingToBuy' },
          })
        }
        ctx.commit(stocked.value, stocked.effects)
        return ctx.done()
      }
      let total = 0
      for (const line of plan) total += line.credits
      if (start.wallet + EPSILON < total) return ctx.refuse(insufficientFunds(total, start.wallet).error)
      buying.add(orderId)
      const outcome = ctx.inFlight(async () => {
        try {
          const fromStock = restock(ctx.career())
          if (fromStock.ok) ctx.commit(fromStock.value, fromStock.effects)
          else if (salvage !== undefined) return fromStock.error
          for (const line of fabBuyPlan(ctx.career(), orderId, market)) {
            const error = await market.commitGoods(here.id, line.id, line.qty, 'buy', (c, fill) => bookFabBuy(c, orderId, line.id, fill))
            if (error !== null) return error
          }
          return null
        } finally {
          buying.delete(orderId)
        }
      })
      if (!outcome.ok) buying.delete(orderId)
      return outcome
    },
  }
}

let unregister: (() => void)[] = []

const feature: GameFeature = {
  name: 'fab',
  order: ORDER,
  init(ctx: GameContext): void {
    // Read lazily: the strategic link provides its client after this feature's init.
    const views = (): readonly StationView[] | undefined => ctx.services.maybe('strategic')?.systemView?.stations
    const handlers = createFabHandlers(
      (station) => berthOwner(station, views(), ctx.reach?.session.layout ?? null),
      (craft) => {
        const reach = ctx.reach
        return reach === null ? null : fabRemoteWorld(reach, views(), reach.session.layout, craft.data, craft.now)
      },
    )
    unregister = [
      registerCraftHandler('fab.start', handlers.start),
      registerCraftHandler('fab.cancel', handlers.cancel),
      registerCraftHandler('fab.complete', handlers.complete),
      registerCraftHandler('fab.deliver', handlers.deliver),
    ]
  },
  dispose(): void {
    for (const off of unregister) off()
    unregister = []
  },
}

export default feature
